import { Annotation, EditorSelection, EditorState, Transaction } from "@codemirror/state";
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  drawSelection,
} from "@codemirror/view";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { sql, SQLite } from "@codemirror/lang-sql";
import {
  HighlightStyle,
  indentOnInput,
  indentUnit,
  syntaxHighlighting,
} from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import { codeEditorLintExtensions } from "./codeEditorLint";

export type CodeEditorLanguage = "python" | "javascript" | "html" | "css" | "sql";

export type CodeEditorHandle = {
  getValue: () => string;
  setValue: (v: string) => void;
  /**
   * Подставить текст, пришедший от другого участника: меняется только
   * отличающийся кусок, курсор и выделение сдвигаются вместе с текстом.
   * Не считается вводом (не уходит обратно на сервер) и не попадает в undo.
   */
  applyRemote: (v: string) => void;
  focus: () => void;
  destroy: () => void;
  isFocused: () => boolean;
};

const editorByRoot = new WeakMap<HTMLElement, CodeEditorHandle>();

/** Помечает транзакции с чужим текстом, чтобы onInput их не отправлял. */
const remoteEdit = Annotation.define<boolean>();

/** Минимальная замена: общий префикс и суффикс остаются нетронутыми. */
export function diffReplace(
  prev: string,
  next: string
): { from: number; to: number; insert: string } | null {
  if (prev === next) return null;
  let start = 0;
  const minLen = Math.min(prev.length, next.length);
  while (start < minLen && prev.charCodeAt(start) === next.charCodeAt(start)) start++;
  let endPrev = prev.length;
  let endNext = next.length;
  while (
    endPrev > start &&
    endNext > start &&
    prev.charCodeAt(endPrev - 1) === next.charCodeAt(endNext - 1)
  ) {
    endPrev--;
    endNext--;
  }
  return { from: start, to: endPrev, insert: next.slice(start, endNext) };
}

/** Тёмная палитра (One Dark–подобная): функции, ключевые слова, строки, числа. */
const wbSyntaxHighlight = HighlightStyle.define([
  { tag: [t.comment, t.lineComment, t.blockComment, t.docComment], color: "#6b7280", fontStyle: "italic" },
  { tag: [t.keyword, t.controlKeyword, t.modifier], color: "#c678dd" },
  { tag: [t.definitionKeyword, t.moduleKeyword, t.operatorKeyword], color: "#c678dd" },
  { tag: [t.string, t.special(t.string), t.character, t.regexp], color: "#98c379" },
  { tag: t.escape, color: "#56b6c2" },
  { tag: [t.number, t.bool, t.null, t.atom, t.literal], color: "#d19a66" },
  { tag: [t.function(t.variableName), t.function(t.propertyName)], color: "#e5c07b" },
  { tag: t.definition(t.function(t.variableName)), color: "#61afef" },
  { tag: [t.className, t.definition(t.className), t.typeName, t.namespace], color: "#e5c07b" },
  { tag: [t.definition(t.variableName), t.local(t.variableName)], color: "#e06c75" },
  { tag: t.special(t.variableName), color: "#e5c07b" },
  { tag: t.variableName, color: "#abb2bf" },
  { tag: [t.definition(t.propertyName), t.propertyName], color: "#e06c75" },
  {
    tag: [
      t.operator,
      t.arithmeticOperator,
      t.logicOperator,
      t.compareOperator,
      t.bitwiseOperator,
      t.updateOperator,
      t.definitionOperator,
      t.derefOperator,
    ],
    color: "#56b6c2",
  },
  {
    tag: [t.punctuation, t.separator, t.bracket, t.paren, t.squareBracket, t.brace, t.angleBracket],
    color: "#abb2bf",
  },
  { tag: t.meta, color: "#abb2bf" },
  { tag: t.labelName, color: "#61afef" },
  { tag: t.invalid, color: "#ff6b6b", textDecoration: "underline wavy" },
  { tag: t.link, color: "#61afef", textDecoration: "underline" },
  { tag: t.heading, fontWeight: "bold", color: "#e5c07b" },
  { tag: t.emphasis, fontStyle: "italic" },
  { tag: t.strong, fontWeight: "bold" },
  { tag: t.strikethrough, textDecoration: "line-through" },
  { tag: t.inserted, color: "#98c379" },
  { tag: t.deleted, color: "#e06c75" },
]);

const wbCodeTheme = EditorView.theme(
  {
    "&": {
      fontSize: "12px",
      fontFamily: 'ui-monospace, "Cascadia Code", Consolas, monospace',
      backgroundColor: "#020617",
      borderRadius: "8px",
      border: "1px solid #334155",
    },
    ".cm-line": {
      color: "#e2e8f0",
    },
    "&.cm-focused": {
      outline: "none",
      borderColor: "#14b8a6",
    },
    ".cm-scroller": {
      overflow: "auto",
      fontFamily: 'inherit',
      lineHeight: "1.45",
      scrollbarWidth: "thin",
      scrollbarColor: "#475569 #0f172a",
    },
    ".cm-content": {
      padding: "6px 0",
      caretColor: "#5eead4",
      minHeight: "64px",
    },
    ".cm-lineNumbers .cm-gutterElement": {
      color: "#475569",
      fontSize: "11px",
      padding: "0 6px 0 8px",
    },
    ".cm-gutters": {
      backgroundColor: "#0f172a",
      borderRight: "1px solid #1e293b",
      color: "#64748b",
    },
    ".cm-activeLine": {
      backgroundColor: "rgba(20, 184, 166, 0.06)",
    },
    ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
      backgroundColor: "rgba(45, 212, 191, 0.22) !important",
    },
    ".cm-cursor": {
      borderLeftColor: "#5eead4",
    },
    ".cm-lintRange-error": {
      backgroundImage:
        "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"6\" height=\"3\"><path d=\"m0 3 l2 -2 l1 0 l2 2 l1 0\" stroke=\"%23f87171\" fill=\"none\" stroke-width=\"1.2\"/></svg>')",
    },
    ".cm-lint-marker-error": {
      content: '"!"',
      color: "#fecaca",
      fontWeight: "700",
    },
    ".cm-gutter-lint": {
      width: "1rem",
    },
    ".cm-gutter-lint .cm-gutterElement": {
      padding: "0 2px",
    },
  },
  { dark: true }
);

/**
 * Home — к началу СТРОКИ, а не к началу видимого куска.
 *
 * Строки здесь переносятся по ширине карточки, и в таком редакторе обычный
 * Home уводит к началу куска, на котором стоит курсор, — человек жмёт его,
 * а оказывается где-то посреди своей же строки. Первое нажатие ставит
 * курсор на первый непробельный символ (для Python это и есть «начало
 * строки»), второе — в самый край, к нулевому столбцу.
 */
function lineStartTarget(line: { from: number; text: string }, head: number): number {
  const firstNonSpace = line.text.search(/\S/);
  const smart = firstNonSpace < 0 ? line.from : line.from + firstNonSpace;
  return head === smart ? line.from : smart;
}

function smartLineStart(view: EditorView): boolean {
  view.dispatch(
    view.state.changeByRange((range) => {
      const line = view.state.doc.lineAt(range.head);
      return { range: EditorSelection.cursor(lineStartTarget(line, range.head)) };
    }),
    { scrollIntoView: true }
  );
  return true;
}

function smartLineStartSelect(view: EditorView): boolean {
  view.dispatch(
    view.state.changeByRange((range) => {
      const line = view.state.doc.lineAt(range.head);
      return {
        range: EditorSelection.range(range.anchor, lineStartTarget(line, range.head)),
      };
    }),
    { scrollIntoView: true }
  );
  return true;
}

/** End — к концу строки целиком, по той же причине. */
function lineEnd(view: EditorView): boolean {
  view.dispatch(
    view.state.changeByRange((range) => {
      const line = view.state.doc.lineAt(range.head);
      return { range: EditorSelection.cursor(line.to) };
    }),
    { scrollIntoView: true }
  );
  return true;
}

function lineEndSelect(view: EditorView): boolean {
  view.dispatch(
    view.state.changeByRange((range) => {
      const line = view.state.doc.lineAt(range.head);
      return { range: EditorSelection.range(range.anchor, line.to) };
    }),
    { scrollIntoView: true }
  );
  return true;
}

function languageExt(lang: CodeEditorLanguage) {
  switch (lang) {
    case "python":
      return python();
    case "javascript":
      return javascript();
    case "html":
      return html();
    case "css":
      return css();
    case "sql":
      return sql({ dialect: SQLite, upperCaseKeywords: true });
  }
}

export function mountCodeEditor(
  mountEl: HTMLElement,
  opts: {
    language: CodeEditorLanguage;
    value: string;
    readOnly: boolean;
    minHeight?: string;
    onInput?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
  }
): CodeEditorHandle {
  const extensions = [
    lineNumbers(),
    highlightActiveLine(),
    drawSelection(),
    history(),
    // Отступ в четыре пробела: в Python отступ — часть синтаксиса, и табом
    // его ставить привычнее всего. Сам Tab в редакторе не был назначен
    // вовсе — отступы набирали пробелами вручную.
    indentUnit.of("    "),
    indentOnInput(),
    // Свои сочетания идут ПЕРЕД общими: иначе выигрывают общие.
    keymap.of([
      indentWithTab,
      { key: "Home", run: smartLineStart, shift: smartLineStartSelect },
      { key: "End", run: lineEnd, shift: lineEndSelect },
      ...defaultKeymap,
      ...historyKeymap,
    ]),
    languageExt(opts.language),
    syntaxHighlighting(wbSyntaxHighlight, { fallback: true }),
    ...codeEditorLintExtensions(opts.language),
    wbCodeTheme,
    EditorView.lineWrapping,
    EditorView.updateListener.of((u) => {
      if (!u.docChanged) return;
      if (u.transactions.every((tr) => tr.annotation(remoteEdit))) return;
      opts.onInput?.();
    }),
    EditorView.domEventHandlers({
      focus: () => {
        opts.onFocus?.();
        return false;
      },
      blur: () => {
        opts.onBlur?.();
        return false;
      },
    }),
  ];

  if (opts.readOnly) {
    extensions.push(EditorState.readOnly.of(true));
  }

  if (opts.minHeight) {
    extensions.push(
      EditorView.theme({
        ".cm-content, .cm-gutter": { minHeight: opts.minHeight },
      })
    );
  }

  const state = EditorState.create({
    doc: opts.value,
    extensions,
  });

  const view = new EditorView({
    state,
    parent: mountEl,
  });

  const handle: CodeEditorHandle = {
    getValue: () => view.state.doc.toString(),
    setValue: (v: string) => {
      if (v === view.state.doc.toString()) return;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: v },
      });
    },
    applyRemote: (v: string) => {
      const change = diffReplace(view.state.doc.toString(), v);
      if (!change) return;
      view.dispatch({
        changes: change,
        annotations: [remoteEdit.of(true), Transaction.addToHistory.of(false)],
      });
    },
    focus: () => view.focus(),
    destroy: () => {
      editorByRoot.delete(mountEl);
      view.destroy();
    },
    isFocused: () => view.hasFocus,
  };

  editorByRoot.set(mountEl, handle);
  return handle;
}

export function getCodeEditor(root: HTMLElement): CodeEditorHandle | undefined {
  return editorByRoot.get(root);
}

export function readCodeValuesFromCard(
  card: HTMLElement,
  language: "python" | "javascript" | "web"
): { source?: string; js?: string; html?: string; css?: string } {
  const out: { source?: string; js?: string; html?: string; css?: string } = {};
  const editors = card.querySelectorAll<HTMLElement>("[data-code-editor]");
  for (const el of editors) {
    const field = el.dataset.codeField;
    const ed = getCodeEditor(el);
    const val = ed?.getValue() ?? "";
    if (field === "source") out.source = val;
    if (field === "js") out.js = val;
    if (field === "html") out.html = val;
    if (field === "css") out.css = val;
  }
  if (language === "python" && out.source === undefined) {
    const ta = card.querySelector<HTMLTextAreaElement>('textarea[data-code-field="source"]');
    if (ta) out.source = ta.value;
  }
  if (language === "javascript" && out.js === undefined) {
    const ta = card.querySelector<HTMLTextAreaElement>('textarea[data-code-field="js"]');
    if (ta) out.js = ta.value;
  }
  return out;
}
